import React, { useState, useEffect } from"react";
import { createPortal } from"react-dom";
import {
 AcademicCapIcon,
 CpuChipIcon,
 CommandLineIcon,
 StarIcon,
 RectangleStackIcon,
 ClipboardDocumentCheckIcon,
 CodeBracketSquareIcon,
 TrophyIcon,
 BookOpenIcon,
 PlayCircleIcon,
} from"@heroicons/react/24/outline";
import { ChevronDownIcon } from"@heroicons/react/24/solid";
import API_BASE_URL from"../Config/config";

/* -------------------------------
 Scroll-trigger hook (open once)
-------------------------------- */
function useScrollOpenOnce(threshold = 0.35, storageKey ="sg_form_shown_skillgenie") {
 const [open, setOpen] = useState(false);

 useEffect(() => {
 if (typeof window ==="undefined") return;

 // Skip if already shown in this tab
 try {
 if (sessionStorage.getItem(storageKey) ==="1") return;
 } catch {}

 const getHeights = () => {
 const de = document.documentElement;
 const body = document.body;
 const scrollHeight = Math.max(de.scrollHeight, body?.scrollHeight || 0);
 const innerH = window.innerHeight || de.clientHeight || 1;
 return { scrollHeight, innerH };
 };

 const onScroll = () => {
 const { scrollHeight, innerH } = getHeights();
 const denom = Math.max(1, scrollHeight - innerH); // avoid /0
 const progress = window.scrollY / denom; // 0..1 precise page progress

 if (progress >= threshold) {
 setTimeout(() => setOpen(true), 200);
 try { sessionStorage.setItem(storageKey,"1"); } catch {}
 window.removeEventListener("scroll", onScroll);
 }
 };

 window.addEventListener("scroll", onScroll, { passive: true });
 onScroll(); // run once in case we already meet the threshold
 return () => window.removeEventListener("scroll", onScroll);
 }, [threshold, storageKey]);

 return [open, setOpen];
}

/* ---------------------------------------
 Submission Form Modal (Portal)
---------------------------------------- */
function SubmissionFormModal({ open, onClose }) {
 const [form, setForm] = useState({
 name:"",
 email:"",
 phone:"",
 message:"",
 });
 const [sent, setSent] = useState(false);

 // Lock background scroll when open
 useEffect(() => {
 if (!open) return;
 const prev = document.body.style.overflow;
 document.body.style.overflow ="hidden";
 return () => {
 document.body.style.overflow = prev;
 };
 }, [open]);

 // Close on ESC
 useEffect(() => {
 const onKey = (e) => e.key ==="Escape" && onClose?.();
 if (open) window.addEventListener("keydown", onKey);
 return () => window.removeEventListener("keydown", onKey);
 }, [open, onClose]);

 const handleChange = (e) =>
 setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

 const handleSubmit = async (e) => {
 e.preventDefault();
 try {
 setSent(true); // show"Sending..."
 const res = await fetch(`${API_BASE_URL}/lead`, {
 method:"POST",
 headers: {"Content-Type":"application/json" },
 mode:"cors",
 body: JSON.stringify(form),
 });

 if (!res.ok) {
 // optional: read error details
 const err = await res.json().catch(() => ({}));
 throw new Error(err.error || `Request failed (${res.status})`);
 }

 // success — clear form, notify, close
 setForm({ name:"", email:"", phone:"", message:"" });
 setTimeout(() => {
 setSent(false);
 onClose?.();
 }, 700);
 } catch (err) {
 console.error("Lead submit error:", err);
 alert(err.message ||"Unable to submit right now.");
 setSent(false);
 }
};

 if (!open) return null;

 const modal = (
 <div
 className="fixed inset-0 z-[9999] flex items-center justify-center"
 role="dialog"
 aria-modal="true"
 >
 {/* Backdrop */}
 <div
 className="absolute inset-0 bg-bgMain/80 backdrop-blur-sm"
 onClick={onClose}
 />

 {/* Dialog */}
 <div className="relative w-[94%] max-w-4xl mx-auto">
 <div className="rounded-2xl bg-brand-midnight border border-secondary shadow-hero overflow-hidden">
 <button
 onClick={onClose}
 className="absolute right-3 top-3 h-9 w-9 rounded-full border border-secondary text-primary hover:bg-secondary/10"
 aria-label="Close"
 >
 ✕
 </button>

 <div className="grid grid-cols-1 md:grid-cols-[0.9fr_1.6fr]">
 {/* Left – Title + Logo */}
 <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-primary/60">
 <div className="rounded-xl border border-primary/50 p-6 h-full flex flex-col items-center justify-center text-center bg-brand-midnight">
 <p className="text-primary font-semibold tracking-wider text-xs">
 SUBMIT YOUR
 </p>
 <h3 className="text-white text-xl font-extrabold mt-1">FORM</h3>

 {/* Logo */}
 <div className="mt-6 h-48 w-48 md:h-[13rem] md:w-[13rem] rounded-full bg-[#0e0e0e] border-2 border-secondary shadow-inner overflow-hidden flex items-center justify-center">
 <img
 src="/logoo.jpg"
 alt="Logo"
 className="h-full w-full object-contain scale-[1.35]"
 />
 </div>
 </div>
 </div>

 {/* Right – Form */}
 <div className="p-6 md:p-8">
 <form onSubmit={handleSubmit} className="space-y-4">
 <input
 name="name"
 placeholder="Name"
 value={form.name}
 onChange={handleChange}
 className="w-full rounded-lg bg-bgMain/60 text-white placeholder-gray-400 border border-primary/70 focus:border-primary outline-none px-4 py-3"
 required
 />
 <input
 name="email"
 type="email"
 placeholder="Email"
 value={form.email}
 onChange={handleChange}
 className="w-full rounded-lg bg-bgMain/60 text-white placeholder-gray-400 border border-primary/70 focus:border-primary outline-none px-4 py-3"
 required
 />
 <input
 name="phone"
 placeholder="Phone Number"
 value={form.phone}
 onChange={handleChange}
 className="w-full rounded-lg bg-bgMain/60 text-white placeholder-gray-400 border border-primary/70 focus:border-primary outline-none px-4 py-3"
 />
 <textarea
 name="message"
 placeholder="Message"
 rows={5}
 value={form.message}
 onChange={handleChange}
 className="w-full rounded-lg bg-bgMain/60 text-white placeholder-gray-400 border border-primary/70 focus:border-primary outline-none px-4 py-3"
 />
 <button
 type="submit"
 className="w-full rounded-full bg-primary text-white font-semibold py-3 hover:bg-primary active:scale-[0.99] transition shadow-[0_6px_24px_rgba(253,224,71,0.25)]"
 >
 {sent ?"Sending..." :"Send"}
 </button>
 {sent && (
 <div className="text-center text-green-400 text-sm">
 Submitted successfully!
 </div>
 )}
 </form>
 </div>
 </div>
 </div>
 </div>
 </div>
 );

 return createPortal(modal, document.body);
}

/* -------------------------------
 HERO (with bg image only)
-------------------------------- */
function Hero({ onOpenForm }) {
 return (
 <section className="relative isolate overflow-hidden">
 {/* Background image */}
 <div
 className="absolute inset-0 -z-10 bg-center bg-cover"
 style={{ backgroundImage:"url('/bg.jpg')" }}
 />
 {/* Dark overlay */}
 <div className="absolute inset-0 -z-10 bg-bgMain/60" />

 <div className="mx-auto max-w-7xl px-4 pt-24 pb-32 text-center text-white">
 <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
 SkillGenie — Learn, Practice &{""}
 <span className="text-accent">Crack Your Dream Job</span> 🚀
 </h1>
 <p className="mt-5 text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
 Bootcamps, Challenges, Role-based Launchpads, Technology Docs, and
 Certifications — everything you need to become job-ready in one place.
 </p>

 <div className="mt-8 flex justify-center gap-4">
 <button
 onClick={onOpenForm}
 className="inline-flex items-center gap-2 rounded-md bg-accent px-6 py-3 font-semibold text-black shadow hover:bg-accent"
 >
 Join for Free
 </button>
 <a
 href="#explainer"
 className="inline-flex items-center gap-2 rounded-md border border-white/80 bg-white/10 px-6 py-3 font-semibold text-white backdrop-blur hover:bg-white/20"
 >
 Learn More
 </a>
 </div>
 </div>
 </section>
 );
}

/* -------------------------------
 EXPLAINER VIDEO (separate section)
-------------------------------- */
function ExplainerVideo() {
 return (
 <section id="explainer" className="bg-gray-50">
 <div className="mx-auto max-w-5xl px-4 py-16 text-center">
 <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6">
 Watch How <span className="text-indigo-600">SkillGenie</span> Works
 </h2>
 <p className="mb-8 text-gray-600">
 A quick walkthrough of our platform, features, and benefits.
 </p>

 {/* Video Embed */}
 <div className="aspect-video w-full overflow-hidden rounded-xl shadow-xl ring-1 ring-black/10">
 <iframe
 className="h-full w-full"
 src="/video.mp4"
 title="SkillGenie — Explainer"
 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
 referrerPolicy="strict-origin-when-cross-origin"
 allowFullScreen
 />
 </div>
 </div>
 </section>
 );
}

/* ---------------------------------------
 PAIN POINTS — Student/Graduate Challenges
---------------------------------------- */
function PainPoints() {
 const points = [
 {
 icon: <RectangleStackIcon className="h-6 w-6" />,
 title:"Don’t know what to study?",
 desc:"Launchpads provide curated, role-based roadmaps so you don’t guess.",
 },
 {
 icon: <ClipboardDocumentCheckIcon className="h-6 w-6" />,
 title:"No projects to showcase?",
 desc:"Bootcamps help you build portfolio-ready, review-driven real projects.",
 },
 {
 icon: <CodeBracketSquareIcon className="h-6 w-6" />,
 title:"Fear coding interviews?",
 desc:"Timed challenges with hidden test cases simulate real pressure.",
 },
 ];

 return (
 <section className="bg-white">
 <div className="mx-auto max-w-7xl px-4 py-16">
 <h2 className="text-center text-3xl md:text-4xl font-extrabold text-gray-900 mb-10">
 Common Career Challenges We Solve
 </h2>
 <div className="grid md:grid-cols-3 gap-6">
 {points.map((p, i) => (
 <div
 key={i}
 className="rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition"
 style={{ backgroundColor:"#eef6ff" }}
 >
 <div className="h-1.5 w-full rounded-t-xl bg-emerald-500" />
 <div className="p-6">
 <div className="text-gray-700">{p.icon}</div>
 <h3 className="mt-3 text-lg font-extrabold text-gray-900 leading-snug">
 {p.title}
 </h3>
 <p className="mt-2 text-[14.5px] text-gray-600">{p.desc}</p>
 </div>
 </div>
 ))}
 </div>
 </div>
 </section>
 );
}

/* ---------------------------------------
 WHY CHOOSE — SkillGenie Features (colorful cards)
---------------------------------------- */
function WhyChoose() {
 const items = [
 {
 icon: <AcademicCapIcon className="h-6 w-6" />,
 title:"Bootcamp Projects",
 desc:"15-phase projects with GitHub integration & mentor reviews.",
 color:"from-indigo-500 to-indigo-700",
 },
 {
 icon: <BookOpenIcon className="h-6 w-6" />,
 title:"Launchpad Guides",
 desc:"Role-based docs + MCQs to guide your career path.",
 color:"from-emerald-500 to-emerald-700",
 },
 {
 icon: <CommandLineIcon className="h-6 w-6" />,
 title:"Technology Docs",
 desc:"Structured learning from basics to advanced stacks.",
 color:"from-pink-500 to-pink-700",
 },
 {
 icon: <CpuChipIcon className="h-6 w-6" />,
 title:"Practice Compiler",
 desc:"Run code in-browser with instant feedback.",
 color:"from-yellow-500 to-yellow-700",
 },
 {
 icon: <TrophyIcon className="h-6 w-6" />,
 title:"Challenges & MCQs",
 desc:"Benchmark with hidden test cases & timed challenges.",
 color:"from-purple-500 to-purple-700",
 },
 {
 icon: <StarIcon className="h-6 w-6" />,
 title:"Certificates & Leaderboards",
 desc:"Earn verifiable certificates & badges.",
 color:"from-teal-500 to-teal-700",
 },
 ];

 return (
 <section className="bg-gray-50 py-20">
 <div className="mx-auto max-w-7xl px-4">
 <h2 className="text-center text-3xl md:text-4xl font-extrabold text-gray-900">
 Why Choose <span className="text-indigo-600">SkillGenie?</span>
 </h2>
 <div className="mt-12 grid md:grid-cols-3 gap-8">
 {items.map((it, idx) => (
 <div
 key={idx}
 className={`rounded-2xl p-6 text-white shadow-lg transform hover:scale-105 transition bg-gradient-to-br ${it.color}`}
 >
 <div className="h-12 w-12 rounded-lg bg-white/20 flex items-center justify-center mb-3">
 {it.icon}
 </div>
 <h3 className="text-lg font-bold">{it.title}</h3>
 <p className="mt-2 text-sm opacity-90">{it.desc}</p>
 </div>
 ))}
 </div>
 </div>
 </section>
 );
}

/* ---------------------------------------
 PROGRAMS — Bootcamp / Launchpad / Challenges
---------------------------------------- */
function Programs() {
 const cards = [
 {
 icon: <AcademicCapIcon className="h-6 w-6" />,
 title:"Bootcamps",
 desc:"Team-based projects with 15 phases & GitHub repos.",
 color:"bg-primary text-primary",
 },
 {
 icon: <BookOpenIcon className="h-6 w-6" />,
 title:"Launchpads (Docs + MCQs)",
 desc:"Role-based guides with curated reading & questions.",
 color:"bg-emerald-100 text-emerald-800",
 },
 {
 icon: <TrophyIcon className="h-6 w-6" />,
 title:"Challenges",
 desc:"Mystery problems with hidden test cases & leaderboards.",
 color:"bg-orange-100 text-orange-800",
 },
 ];

 return (
 <section className="bg-white py-20">
 <div className="mx-auto max-w-7xl px-4">
 <h2 className="text-center text-3xl md:text-4xl font-extrabold text-gray-900">
 Core Programs
 </h2>
 <div className="mt-12 grid gap-6 md:grid-cols-3">
 {cards.map((c, i) => (
 <div
 key={i}
 className={`rounded-2xl p-6 shadow-md hover:shadow-xl hover:scale-105 transition ${c.color}`}
 >
 <div className="flex items-center gap-3">
 <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center">
 {c.icon}
 </div>
 <h3 className="font-semibold">{c.title}</h3>
 </div>
 <p className="mt-3 text-sm">{c.desc}</p>
 </div>
 ))}
 </div>
 </div>
 </section>
 );
}

/* ---------------------------------------
 TESTIMONIALS — Learner Stories
---------------------------------------- */
function Testimonials() {
 const cards = [
 {
 quote:
"Structured mentorship and weekly reviews helped me crack my first job.",
 by:"Aarav • Data Analyst",
 },
 {
 quote:
"Project-first learning gave me confidence to show real work in interviews.",
 by:"Maya • Full-Stack Dev",
 },
 {
 quote:
"Practical ML + deployment made the difference. Offer in 10 weeks.",
 by:"Rohit • ML Engineer",
 },
 ];

 return (
 <section className="bg-brand-midnight text-white">
 <div className="mx-auto max-w-7xl px-4 py-16">
 <h2 className="text-3xl font-extrabold text-center mb-8">
 Success Stories
 </h2>
 <div className="grid md:grid-cols-3 gap-6">
 {cards.map((c, i) => (
 <div
 key={i}
 className="rounded-xl p-5 border border-white/30 hover:bg-white/[0.05] transition"
 >
 <p className="text-sm text-white/85">“{c.quote}”</p>
 <div className="mt-4 text-[12px] text-white/60">{c.by}</div>
 </div>
 ))}
 </div>
 </div>
 </section>
 );
}

/* ---------------------------------------
 OUTCOMES — Job/Portfolio outcomes
---------------------------------------- */
function Outcomes() {
 const items = [
 {
 color:"bg-rose-500",
 value:"100+",
 title:"Portfolio Projects",
 desc:"Completed and showcased by learners.",
 },
 {
 color:"bg-accent",
 value:"Verified",
 title:"Certificates",
 desc:"QR verifiable & recruiter-trusted.",
 },
 {
 color:"bg-teal-500",
 value:"Job-Ready",
 title:"Skills",
 desc:"Mastery of coding + real projects.",
 },
 ];
 return (
 <section className="bg-white">
 <div className="mx-auto max-w-7xl px-4 py-16">
 <h2 className="text-center text-3xl md:text-4xl font-extrabold text-gray-900">
 Outcomes You Can Expect
 </h2>
 <div className="grid md:grid-cols-3 gap-6 mt-8">
 {items.map((it, i) => (
 <div key={i} className="rounded-xl overflow-hidden shadow-sm">
 <div className={`${it.color} relative px-6 pt-10 pb-6 text-white`}>
 <div className="text-[30px] font-extrabold">{it.value}</div>
 <div className="text-[13px] opacity-95">{it.title}</div>
 </div>
 <div className="bg-white p-6 border-x border-b border-gray-100">
 <p className="text-sm text-gray-600">{it.desc}</p>
 </div>
 </div>
 ))}
 </div>
 </div>
 </section>
 );
}

/* ---------------------------------------
 HOW IT WORKS — SkillGenie flow
---------------------------------------- */
function HowItWorks() {
 const steps = [
 { title:"1. Start with Launchpad", desc:"Choose your career role, explore docs & MCQs." },
 { title:"2. Learn Tech", desc:"Dive into structured technology docs & guided practice." },
 { title:"3. Practice & Challenges", desc:"Run code in the compiler and solve hidden test cases." },
 { title:"4. Bootcamp & Certification", desc:"Team projects, mentor reviews & verifiable certificates." },
 ];
 return (
 <section className="bg-brand-midnight text-white">
 <div className="mx-auto max-w-7xl px-4 py-16">
 <h2 className="text-3xl font-extrabold text-center mb-8">
 How SkillGenie Works
 </h2>
 <div className="grid md:grid-cols-2 gap-6">
 {steps.map((s, i) => (
 <div
 key={i}
 className="rounded-xl border border-primary/30 bg-[#101b39] p-6 hover:bg-[#0f1a33] transition"
 >
 <h3 className="text-lg font-bold text-teal-200">{s.title}</h3>
 <p className="mt-2 text-sm text-white/80">{s.desc}</p>
 </div>
 ))}
 </div>
 </div>
 </section>
 );
}

/* ---------------------------------------
 FAQ — Collapsible
---------------------------------------- */
function FAQ() {
 const faqs = [
 {
 question:"What is SkillGenie?",
 answer:
"SkillGenie is a complete coding practice and career preparation platform. We combine bootcamps, coding challenges, role-based launchpads, technology docs, and certifications into one ecosystem. Unlike scattered online tutorials, SkillGenie provides a guided path from beginner to job-ready professional. You not only learn concepts but also apply them in real-world projects, get feedback from mentors, and validate your progress with verifiable certificates.",
 },
 {
 question:"Who can join?",
 answer:
"Our platform is designed for students, recent graduates, and working professionals looking to upskill or switch careers. Whether you are completely new to coding, preparing for interviews, or trying to build a portfolio, SkillGenie has structured tracks for each stage. We also provide advanced challenges for experienced developers who want to sharpen their problem-solving skills and stay competitive in tech interviews.",
 },
 {
 question:"Is it free?",
 answer:
"Yes! You can sign up for free and instantly access selected coding challenges, quizzes, and technology docs. This helps you explore our platform before committing to premium features. If you want to unlock advanced bootcamps, mentor feedback, team-based projects, and certification, you can upgrade to our premium plans. This ensures everyone can start learning without barriers, while serious learners can go deeper with full resources.",
 },
 {
 question:"Do I get a certificate?",
 answer:
"Absolutely. SkillGenie provides QR-verifiable certificates and digital badges that you can showcase on LinkedIn, GitHub, or resumes. These aren’t just participation certificates — they validate your project completions, challenges solved, and technology stacks mastered. Recruiters and companies trust verifiable credentials, which makes your profile stand out in the job market.",
 },
 {
 question:"How are Bootcamps structured?",
 answer:
"Each bootcamp follows a 15-phase structure designed to simulate real workplace projects. You’ll start with requirement gathering, move through design and implementation, and finish with testing and deployment. Along the way, mentors provide reviews and guidance. Bootcamps are integrated with GitHub, so by the end, you’ll have a professional-grade project to showcase to recruiters. This ensures you not only learn but also build proof of your skills.",
 },
 ];

 const [openIndex, setOpenIndex] = useState(null);

 return (
 <section className="bg-bgMain py-16">
 <div className="max-w-4xl mx-auto px-4">
 <h2 className="text-3xl font-extrabold text-center mb-10 text-primary">
 Frequently Asked Questions
 </h2>
 <div className="space-y-4">
 {faqs.map((faq, idx) => (
 <div
 key={idx}
 className={`border rounded-xl shadow-md transition ${
 openIndex === idx
 ?"bg-secondary/10 border-primary"
 :"bg-neutral-900 border-neutral-700"
 }`}
 >
 <button
 onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
 className="flex items-center justify-between w-full px-6 py-4 text-left font-semibold text-primary hover:text-primary"
 >
 <span>{faq.question}</span>
 <ChevronDownIcon
 className={`h-5 w-5 transform transition-transform ${
 openIndex === idx ?"rotate-180 text-primary" :""
 }`}
 />
 </button>
 <div
 className={`px-6 pb-4 text-muted text-[15px] leading-relaxed transition-all duration-500 ease-in-out ${
 openIndex === idx
 ?"max-h-[600px] opacity-100"
 :"max-h-0 opacity-0 overflow-hidden"
 }`}
 >
 {faq.answer}
 </div>
 </div>
 ))}
 </div>
 </div>
 </section>
 );
}

/* ---------------------------------------
 WhatsApp Floating Button
---------------------------------------- */
function WhatsAppButton() {
 return (
 <a
 href="https://wa.me/YOUR_WA_NUMBER" // e.g. 919876543210
 target="_blank"
 rel="noopener noreferrer"
 aria-label="Chat with SkillGenie on WhatsApp"
 className="fixed bottom-5 right-5 z-50 rounded-full bg-green-500 p-4 text-white shadow-xl hover:bg-green-600 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
 >
 <svg
 xmlns="http://www.w3.org/2000/svg"
 viewBox="0 0 32 32"
 className="h-7 w-7"
 fill="currentColor"
 >
 <path d="M19.11 17.24c-.27-.13-1.6-.79-1.84-.88-.24-.09-.42-.13-.6.13-.18.27-.69.88-.85 1.06-.16.18-.31.2-.58.07-.27-.13-1.12-.41-2.14-1.31-.79-.7-1.32-1.56-1.48-1.82-.16-.27-.02-.41.11-.54.11-.11.27-.29.4-.43.13-.14.18-.24.27-.4.09-.16.04-.3-.02-.43-.07-.13-.6-1.43-.82-1.96-.21-.51-.43-.44-.6-.45h-.51c-.16 0-.43.06-.66.3-.23.24-.87.85-.87 2.07s.89 2.4 1.02 2.56c.13.16 1.75 2.68 4.25 3.76.59.26 1.05.41 1.4.52.59.19 1.13.16 1.56.1.48-.07 1.6-.65 1.83-1.28.23-.63.23-1.17.16-1.28-.07-.11-.25-.18-.52-.31z" />
 <path d="M26.07 5.93C23.33 3.19 19.78 1.7 16 1.7 8.47 1.7 2.3 7.87 2.3 15.4c0 2.39.62 4.73 1.81 6.78L2 30.3l8.33-2.08c1.99 1.09 4.23 1.67 6.52 1.67 7.53 0 13.7-6.17 13.7-13.7 0-3.78-1.49-7.33-4.23-10.07zm-10.07 22c-2.06 0-4.07-.55-5.82-1.59l-.42-.25-4.94 1.24 1.32-4.81-.27-.45a12.59 12.59 0 0 1-1.89-6.67c0-6.95 5.65-12.6 12.6-12.6 3.37 0 6.54 1.31 8.93 3.7 2.39 2.39 3.7 5.56 3.7 8.93 0 6.95-5.65 12.6-12.6 12.6z" />
 </svg>
 </a>
 );
}

/* ---------------------------------------
 PAGE
---------------------------------------- */
export default function SkillGenieLandingPage() {
 const [formOpen, setFormOpen] = useScrollOpenOnce(0.35);
 const openForm = () => setFormOpen(true);

 return (
 <main className="font-inter">
 <Hero onOpenForm={openForm} />
 <ExplainerVideo />
 <PainPoints />
 <WhyChoose />
 <Programs />
 <Testimonials />
 <Outcomes />
 <HowItWorks />
 <FAQ />
 {/* <WhatsAppButton /> */}

 {/* Form Modal */}
 <SubmissionFormModal open={formOpen} onClose={() => setFormOpen(false)} />
 </main>
 );
}



